/**
 * useOfflineSync - React hook to subscribe to the offline sync engine state.
 * Returns: { online, pendingCount, lastFlush, flushing, flushNow }
 */
import { useEffect, useState, useCallback } from 'react';
import {
  isOnline as engineOnline,
  subscribeSync,
  getPendingCount,
  flush,
} from '@/services/offlineQueue';

export default function useOfflineSync() {
  const [online, setOnline] = useState(engineOnline());
  const [pendingCount, setPendingCount] = useState(0);
  const [flushing, setFlushing] = useState(false);
  const [lastFlush, setLastFlush] = useState(null);

  const refreshCount = useCallback(async () => {
    const n = await getPendingCount();
    setPendingCount(n);
  }, []);

  useEffect(() => {
    refreshCount();
    const handler = (e) => {
      if (!e || !e.kind) return;
      if (e.kind === 'online')  setOnline(true);
      if (e.kind === 'offline') setOnline(false);
      if (e.kind === 'flush_started')  setFlushing(true);
      if (e.kind === 'flush_done')     {
        setFlushing(false);
        setLastFlush(new Date().toISOString());
        refreshCount();
      }
      if (e.kind === 'flush_skipped_offline' || e.kind === 'flush_unauthorized') {
        setFlushing(false);
      }
      if (e.kind === 'enqueued' || e.kind === 'cleared') {
        refreshCount();
      }
    };
    const unsub = subscribeSync(handler);
    // Also track browser network status changes directly (defensive)
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      unsub();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refreshCount]);

  const flushNow = useCallback(async () => {
    setFlushing(true);
    try {
      return await flush();
    } finally {
      setFlushing(false);
      await refreshCount();
    }
  }, [refreshCount]);

  return { online, pendingCount, flushing, lastFlush, flushNow };
}
